import { skipToken } from '@reduxjs/toolkit/query';
import { formatAddress, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonPageTitle } from '@strata/react-buttons';
import { DollarAmount } from '@strata/react-dollar-amount';
import { ButtonWithDropdownMenu } from '@strata/react-dropdown-menu';
import { FormHeaderAndRowContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { FormTitleLabelValuePair, HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { ServicingRate } from '@strata/react-servicing-rate';
import { useEffect, useState } from 'react';
import { type Location, useLocation, useNavigate, useParams } from 'react-router';

import { ClaimDetails } from 'components/claims/ClaimDetails';
import { CheckClaimStatusDialog } from 'components/claims/dialogs/CheckClaimStatusDialog';
import { VoidClaimDialog } from 'components/claims/dialogs/VoidClaimDialog';
import { Service } from 'components/claims/Service';
import { ContactInfoDialog } from 'components/shared/dialogs/ContactInfoDialog';
import { ProductLabel } from 'components/shared/ProductLabel';
import { usePermissionCheck, useReferralDetails } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { ClaimSubmissionTypes } from 'services/MedicalBillingConstants';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { formatDiagnosisLabel, useGetClaimDetailsQuery } from 'store/api/medbillingApi';
import {
    useGetDiagnosesCodesQuery,
    useGetProductsMapQuery,
    useGetSupplierDetailsQuery,
} from 'store/api/referenceApi';
import { useGetMemberDetailsQuery } from 'store/api/scopeAssessorApi';

// eslint-disable-next-line complexity
export const ClaimDetailsPage = () => {
    const { transactionId } = useParams() as { transactionId: string };
    const navigate = useNavigate();
    const location = useLocation() as Location<{ from?: Location }>;

    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);

    // get selected claim from claims list
    const claimsDataLoaded = useAppSelector((state) => state.claimsList.claimsDataLoaded);
    const selectedClaim = useAppSelector((state) =>
        state.claimsList.claimsData
            .filter((claim) => claim.submissionType === ClaimSubmissionTypes.ADJUDICATE)
            .find((claim) => claim.transactionId === transactionId),
    );

    // navigate back to claims history if transactionId is invalid
    useEffect(() => {
        if (!selectedClaim && claimsDataLoaded) {
            navigate(location.state?.from ?? AppRoutes.claimsHistory, { replace: true });
        }
    }, [selectedClaim, claimsDataLoaded, navigate, location]);

    const { data: diagnosesList } = useGetDiagnosesCodesQuery();
    const { data: productMap } = useGetProductsMapQuery();
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        selectedClaim?.supplierNpi ? { npi: selectedClaim.supplierNpi } : skipToken,
    );
    const referralDetails = useReferralDetails(selectedClaim?.referralId, selectedClaim?.payerId);
    const { data: memberDetails, isFetching: isMemberDetailsFetching } = useGetMemberDetailsQuery(
        referralDetails?.memberId &&
            referralDetails.memberFirstName &&
            referralDetails.memberLastName
            ? {
                  segmentId: referralDetails.segmentId,
                  memberId: referralDetails.memberId,
                  firstName: referralDetails.memberFirstName,
                  lastName: referralDetails.memberLastName,
                  dateOfBirth: referralDetails.memberDOB,
                  payerId: referralDetails.payerId,
              }
            : skipToken,
    );

    const { data: claimDetails } = useGetClaimDetailsQuery(
        selectedClaim ? { transactionId, payerId: selectedClaim.payerId } : skipToken,
    );

    const [checkClaimStatusDialogOpen, setCheckClaimStatusDialogOpen] = useState(false);
    const [voidClaimDialogOpen, setVoidClaimDialogOpen] = useState(false);
    const [contactInfoDialogOpen, setContactInfoDialogOpen] = useState(false);

    const menuItems = [
        ...(writeMedBilling
            ? [
                  {
                      text: 'Check Claim Status',
                      onItemClick: () => setCheckClaimStatusDialogOpen(true),
                  },
                  {
                      text: 'Revise Claim',
                      onItemClick: () =>
                          navigate(AppRoutes.claimSubmissionForm(selectedClaim?.referralId), {
                              state: {
                                  previousEstimate: claimDetails,
                                  isRevision: true,
                              },
                          }),
                  },
                  {
                      text: 'Void Claim',
                      onItemClick: () => setVoidClaimDialogOpen(true),
                  },
              ]
            : []),
        {
            text: 'Print Claim',
            onItemClick: () => window.print(),
        },
    ];

    const additionalMenuItems = [
        {
            text: 'View Patient Contact Information',
            onItemClick: () => setContactInfoDialogOpen(true),
        },
    ];

    return (
        <>
            <FullscreenLoading
                isShowing={!selectedClaim}
                loadingText="Loading claim information..."
            />
            <CheckClaimStatusDialog
                isOpen={checkClaimStatusDialogOpen}
                onClose={() => setCheckClaimStatusDialogOpen(false)}
                claimDetails={claimDetails}
            />
            <VoidClaimDialog
                isOpen={voidClaimDialogOpen}
                onClose={() => setVoidClaimDialogOpen(false)}
                referralDetails={referralDetails}
                claimDetails={claimDetails}
            />
            <ContactInfoDialog
                isOpen={contactInfoDialogOpen}
                onClose={() => setContactInfoDialogOpen(false)}
                memberDetails={memberDetails}
            />
            <FormHeaderAndRowContent
                rowOneContentHeader={
                    <>
                        <ActionButtonPageTitle
                            label="Claims History"
                            title="Claim Details"
                            id="claim-details-page-title-button"
                            onClick={() =>
                                navigate(location.state?.from ?? AppRoutes.claimsHistory)
                            }
                        />
                        <div style={{ marginLeft: 'auto' }}>
                            <ButtonWithDropdownMenu
                                menuItems={menuItems}
                                additionalMenuItems={additionalMenuItems}
                                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
                                label="Actions"
                            />
                        </div>
                    </>
                }
                rowTwoContentHeader={
                    selectedClaim &&
                    !isMemberDetailsFetching && (
                        <>
                            <FormTitleLabelValuePair
                                label={formatNameLastFirst(
                                    memberDetails?.lastName,
                                    memberDetails?.firstName,
                                )}
                                value={formatAddress(
                                    memberDetails?.memberCity,
                                    memberDetails?.memberState,
                                    memberDetails?.memberZip,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Submitted Amount"
                                value={
                                    <DollarAmount
                                        amount={claimDetails?.submittedAmount}
                                        showZeroValue
                                        large
                                    />
                                }
                            />
                            <HeaderLabelValuePair
                                label="Paid Amount"
                                value={
                                    <DollarAmount
                                        amount={claimDetails?.claimTotal}
                                        showZeroValue
                                        large
                                    />
                                }
                            />
                            <HeaderLabelValuePair
                                label="Servicing Rate"
                                value={
                                    <ServicingRate
                                        servicingRate={referralDetails?.winningBid}
                                        referentPriceType={referralDetails?.referentPriceType}
                                    />
                                }
                            />
                            <HeaderLabelValuePair
                                label="Primary Diagnosis"
                                value={formatDiagnosisLabel(
                                    claimDetails?.diagnoses?.[0],
                                    diagnosesList,
                                )}
                            />
                            <HeaderLabelValuePair
                                label="Product"
                                value={
                                    referralDetails && (
                                        <ProductLabel
                                            product={
                                                productMap?.[referralDetails.productServiceOid]
                                                    ?.description
                                            }
                                            isBrand={referralDetails.isBrand}
                                        />
                                    )
                                }
                            />
                        </>
                    )
                }
                rowOneContent={
                    <ClaimDetails
                        claimDetails={claimDetails}
                        supplierDetails={supplierDetails}
                        diagnosesList={diagnosesList}
                    />
                }
                rowTwoContent={
                    <div
                        className="flex__gap--medium"
                        style={{ flexDirection: 'column', width: '100%' }}
                    >
                        {claimDetails?.services?.map((service, index) => (
                            <Service
                                key={service.procedureCode}
                                service={service}
                                index={index}
                                diagnosesList={diagnosesList}
                                referralDetails={referralDetails}
                            />
                        ))}
                    </div>
                }
            />
        </>
    );
};
